import classnames from 'classnames';
import * as React from 'react';
import { memo, useCallback } from 'react';
import { connect } from 'react-redux';
import MediaQuery from 'react-responsive';
import { clearDates, removeSegment, setDate, setDateMultiCity, setLocation, swapLocations, swapMultiLocations, addSegment } from '../../../../store/segments/actions';
import { canAddSegment, canRemoveSegment, getSelectedDates, isMultiCity, makeGetSegment } from '../../../../store/segments/selectors';
import { LocationType } from '../../../../../types';
import Datepicker from './Datepicker/Datepicker';
import LocationField from './Location/Location';
import { addRecentSearch } from '../../../../store/recentSearches/actions';
import { getRecentSearches } from '../../../../store/recentSearches/selectors';
import { useConfig } from '../../../../../context';
import { useTheme } from '../../../../../theme';
import { getPassengersForRequest } from '../../../../store/passengers/selectors';
import { MOBILE_MIN_WIDTH } from '../../../../../utils';
import { useRenderers } from '../../../../../renderProps';
import { CdnIcon } from '@shared/ui';
const datesAreNotEqual = (prevDates, nextDates) => {
    // Quick check.
    if (prevDates.length !== nextDates.length) {
        return true;
    }
    // Slow check.
    const numOfDates = prevDates.length;
    for (let i = 0; i < numOfDates; i++) {
        if (prevDates[i] !== nextDates[i]) {
            return true;
        }
    }
    return false;
};
const Segment = memo(({ isMultiCity, recentSearches, setDate, setDateMultiCity, swapLocations, swapMultiLocations, setLocation, segmentId, segment, selectedDates, clearDates, addRecentSearch, removeSegment, canAddSegment, canRemoveSegment, passengers, addSegment }) => {
    const { Segment: css } = useTheme('SearchForm');
    const { proMode, showPricesInDatepicker, mobileFormType } = useConfig().SearchForm;
    const alternative = mobileFormType !== 'common';
    const isSegmentFirst = segmentId === 0;
    const isControlDisabled = isSegmentFirst ? !canAddSegment : !canRemoveSegment;
    const { SearchFormLocation: SearchFormLocationOverride = LocationField, SearchFormSegmentDatepicker: SearchFormSegmentDatepickerOverride = Datepicker } = useRenderers();
    const onRemoveSegment = useCallback(() => removeSegment(segmentId), [segmentId]);
    const onDateChange = useCallback((date, isBackDate = false) => {
        if (isMultiCity) {
            setDateMultiCity(date, segmentId);
        }
        else {
            setDate(date, isBackDate);
            if (!isBackDate) {
                // Reset back date.
                setDate(null, true);
            }
        }
    }, [segmentId, isMultiCity]);
    const onSetLocation = useCallback((location, type) => {
        if (type === LocationType.Departure && location.iata === (segment.arrival && segment.arrival.iata)) {
            setLocation(null, LocationType.Arrival);
        }
        if (isMultiCity) {
            setLocation(location, type, segmentId);
        }
        else {
            setLocation(location, type);
        }
        addRecentSearch(location);
    }, [segmentId, isMultiCity, segment]);
    const onSwapHandler = useCallback(() => {
        if (isMultiCity) {
            swapMultiLocations(segmentId);
        }
        else {
            swapLocations();
        }
    }, [isMultiCity, segmentId, swapLocations, swapMultiLocations]);
    return (React.createElement("div", { className: classnames(css.segment, { [css.alternative]: alternative, [css.segment_advanced]: proMode }) },
        React.createElement("div", { className: classnames(css.wrapper, { [css.withMultiCity]: isMultiCity }) },
            React.createElement("div", { className: `${css.cell} ${css.cell_location}` },
                React.createElement("div", { className: css.departureInputWrapper },
                    React.createElement(SearchFormLocationOverride, { type: LocationType.Departure, segmentId: segmentId, locations: {
                            departure: segment.departure,
                            arrival: segment.arrival
                        }, setLocation: onSetLocation, onSwap: onSwapHandler, recentSearches: recentSearches, withMultiCity: isMultiCity }))),
            React.createElement("div", { className: `${css.cell} ${css.cell_location} ${css.cell_location_arrival}` },
                React.createElement(SearchFormLocationOverride, { type: LocationType.Arrival, segmentId: segmentId, locations: {
                        departure: segment.departure,
                        arrival: segment.arrival
                    }, setLocation: onSetLocation, recentSearches: recentSearches, withMultiCity: isMultiCity })),
            React.createElement("div", { className: classnames(css.cell, css.cell_date, {
                    [css.cell_fixed]: isMultiCity
                }) },
                React.createElement(SearchFormSegmentDatepickerOverride, { valueClassName: css.datepicker_value, passengers: passengers, dateTo: segment.date, dateBack: segment.dateBack, selectedDates: selectedDates, onDateChange: onDateChange, withDateBack: !isMultiCity, showPrices: showPricesInDatepicker, clearDates: clearDates, segmentId: segmentId, locations: {
                        departure: segment.departure,
                        arrival: segment.arrival
                    } }))),
        isMultiCity && (React.createElement(MediaQuery, { minWidth: MOBILE_MIN_WIDTH },
            React.createElement("div", { onClick: isSegmentFirst && canAddSegment ? addSegment : onRemoveSegment, className: classnames(css.control, {
                    [css.control_disabled]: isControlDisabled
                }) },
                React.createElement(CdnIcon, { icon: isSegmentFirst ? 'Action/Add' : 'Action/Decr' }))))));
}, (prevProps, nextProps) => prevProps.segmentId === nextProps.segmentId &&
    prevProps.segment === nextProps.segment &&
    !datesAreNotEqual(prevProps.selectedDates, nextProps.selectedDates) &&
    prevProps.isMultiCity === nextProps.isMultiCity &&
    prevProps.canRemoveSegment === nextProps.canRemoveSegment &&
    prevProps.canAddSegment === nextProps.canAddSegment);
const mapStateToProps = (state, ownProps) => {
    const getSegment = makeGetSegment(ownProps.segmentId);
    return (state, ownProps) => {
        return Object.assign(Object.assign({}, ownProps), { selectedDates: getSelectedDates(state), isMultiCity: isMultiCity(state), canAddSegment: canAddSegment(state), canRemoveSegment: canRemoveSegment(state), segment: getSegment(state), recentSearches: getRecentSearches(state), passengers: getPassengersForRequest(state) });
    };
};
const mapDispatchToProps = {
    removeSegment,
    setDate,
    setDateMultiCity,
    clearDates,
    setLocation,
    swapLocations,
    swapMultiLocations,
    addRecentSearch,
    addSegment
};
export default connect(mapStateToProps, mapDispatchToProps)(Segment);
